import { useState, useEffect, useCallback } from 'react';
import {
  X,
  CheckCircle2,
  AlertCircle,
  Loader2,
  Zap,
  ArrowRight,
  Lock,
} from 'lucide-react';
import { ethers } from 'ethers';
import { useWallet } from '../contexts/WalletContext';
import { useAuth } from '../contexts/AuthContext';
import { useSubscription, type PlanId, type BillingPeriod, type PaymentCurrency, type PaymentIntent, type PlanDefinition } from '../hooks/useSubscription';
import { SUPPORTED_NETWORK_NAME } from '../config/supportedChain';
import PaymentHistory from './PaymentHistory';

/** Circle USDC brand mark. */
function UsdcIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="16" r="16" fill="#2775CA" />
      <path
        fill="#fff"
        d="M20.022 18.124c0-2.124-1.28-2.86-3.84-3.172v-.004c-1.462-.26-1.76-.618-1.76-1.324 0-.706.528-1.11 1.536-1.11 1.056 0 1.56.396 1.76 1.188l1.738-.374c-.308-1.386-1.386-2.178-2.86-2.42V9.45h-1.716v1.452c-1.848.264-2.992 1.452-2.992 2.992 0 1.98 1.188 2.772 3.696 3.124v.004c1.408.308 1.804.704 1.804 1.408 0 .792-.704 1.32-1.848 1.32-1.452 0-2.2-.66-2.42-1.76l-1.76.396c.352 1.716 1.54 2.64 3.388 2.904v1.496h1.716v-1.496c1.848-.264 3.124-1.452 3.124-3.168zm-7.082 3.376a8.82 8.82 0 0 1-3.696-3.696l1.54-.66a7.12 7.12 0 0 0 3.476 3.476l-.66 1.54a8.82 8.82 0 0 1-.66 0zm6.12 0 .66-1.54a7.12 7.12 0 0 0 3.476-3.476l1.54.66a8.82 8.82 0 0 1-4.356 4.356h-.66zM9.244 14.196l-1.54-.66a8.82 8.82 0 0 1 4.356-4.356l.66 1.54a7.12 7.12 0 0 0-3.476 3.476zm13.512 0a7.12 7.12 0 0 0-3.476-3.476l.66-1.54a8.82 8.82 0 0 1 4.356 4.356l-1.54.66z"
      />
    </svg>
  );
}

/** Tether USDT brand mark. */
function UsdtIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="16" r="16" fill="#26A17B" />
      <path
        fill="#fff"
        d="M17.922 17.383v-.002c-.11.008-.677.042-1.942.042-1.01 0-1.721-.03-1.971-.042v.003c-3.888-.171-6.79-.848-6.79-1.658 0-.809 2.902-1.486 6.79-1.66v2.644c.254.018.982.061 1.988.061 1.207 0 1.812-.05 1.925-.06v-2.643c3.88.173 6.775.85 6.775 1.658 0 .81-2.895 1.485-6.775 1.657m0-3.57v-2.247h5.414V7.813H8.595v3.753h5.414v2.245c-4.4.202-7.709 1.074-7.709 2.118 0 1.044 3.309 1.915 7.709 2.118v7.582h3.913v-7.584c4.393-.202 7.694-1.073 7.694-2.116 0-1.043-3.301-1.914-7.694-2.117"
      />
    </svg>
  );
}

/** Ether ETH brand mark. */
function EthIcon({ className = 'w-5 h-5' }: { className?: string }) {
  return (
    <svg className={className} viewBox="0 0 32 32" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <circle cx="16" cy="16" r="16" fill="#627EEA" />
      <path
        fill="#fff"
        fillOpacity="0.8"
        d="M16.498 4v8.87l7.497 4.35-7.497-11.22z"
      />
      <path
        fill="#fff"
        d="M16.498 4L9 17.22l7.498-4.35V4z"
      />
      <path
        fill="#fff"
        fillOpacity="0.8"
        d="M16.498 21.968v6.027L24 19.616l-7.502 2.352z"
      />
      <path
        fill="#fff"
        d="M16.498 27.995v-6.028L9 19.617l7.498 8.378z"
      />
      <path
        fill="#fff"
        fillOpacity="0.6"
        d="M16.498 20.573l7.497-3.505-7.497-3.586v7.091z"
      />
      <path
        fill="#fff"
        fillOpacity="0.7"
        d="M9 17.068l7.498 3.505v-7.091L9 17.068z"
      />
    </svg>
  );
}

// ─── Props ──────────────────────────────────────────────────────────────────

interface SubscriptionTerminalProps {
  /** Whether the terminal blocks interaction (forced when quota exceeded). */
  forced?: boolean;
  /** Callback after a successful subscription activation. */
  onActivate?: () => void;
  /** Callback when the user closes the terminal (only when not forced). */
  onClose?: () => void;
}

type PaymentStep = 'select' | 'paying' | 'verify' | 'success' | 'error';

// ─── Component ──────────────────────────────────────────────────────────────

const USDT_CONTRACT_ADDRESS = '0xdAC17F958D2ee523a2206206994597C13D831ec7';
const USDC_CONTRACT_ADDRESS = '0xA0b86991c6218b36c1d19D4a2e9Eb0cE3606eB48';

/** Selected-state metallic theme per paid plan. */
const PLAN_METAL: Record<
  Exclude<PlanId, 'free'>,
  { card: string; accent: string; section: string }
> = {
  starter: {
    // Copper
    card: 'bg-[#F3E0D0] dark:bg-[#5C3A22]/40 border-[#B87333] ring-2 ring-[#B87333]/35',
    accent: 'text-[#8B4513] dark:text-[#E0A96D]',
    section: 'bg-[#F3E0D0]/70 dark:bg-[#5C3A22]/30 border-[#B87333]/40',
  },
  extensive: {
    // Silver
    card: 'bg-[#E8E9EB] dark:bg-[#3A3D42]/50 border-[#A8A9AD] ring-2 ring-[#C0C0C0]/40',
    accent: 'text-[#4B5563] dark:text-[#D1D5DB]',
    section: 'bg-[#E8E9EB]/70 dark:bg-[#3A3D42]/30 border-[#A8A9AD]/40',
  },
  unlimited: {
    // Gold
    card: 'bg-[#F8EFC8] dark:bg-[#5C4A12]/40 border-[#D4AF37] ring-2 ring-[#D4AF37]/40',
    accent: 'text-[#8B6914] dark:text-[#F0D77B]',
    section: 'bg-[#F8EFC8]/70 dark:bg-[#5C4A12]/30 border-[#D4AF37]/40',
  },
};

function periodSuffix(period?: BillingPeriod | null): string {
  if (period === 'weekly') return '/week';
  if (period === 'yearly') return '/year';
  return '/month';
}

/** Sepolia (chain 11155111) explorer; any other chain falls back to mainnet. */
function explorerTxUrl(txHash: string, chainId?: number): string {
  const base = chainId === 11155111 ? 'https://sepolia.etherscan.io' : 'https://etherscan.io';
  return `${base}/tx/${txHash}`;
}

/**
 * Prefer the backend-provided token address (chain-aware, may be a Sepolia mock
 * USDC/USDT for testing). Falls back to the well-known mainnet addresses so the
 * flow keeps working on mainnet or when the intent predates token addresses.
 * ETH is native — no token address.
 */
function resolveTokenAddress(intent: PaymentIntent, selectedCurrency: PaymentCurrency): string {
  if (selectedCurrency === 'ETH') return '';
  if (intent.tokenAddress) return intent.tokenAddress;
  return selectedCurrency === 'USDT' ? USDT_CONTRACT_ADDRESS : USDC_CONTRACT_ADDRESS;
}

/** Format a wei amount as a user-friendly ETH string (e.g. "0.0342 ETH"). */
function formatEthAmount(amountToken: string | null): string | null {
  if (!amountToken) return null;
  try {
    const wei = BigInt(amountToken);
    if (wei === 0n) return null;
    const eth = Number(wei) / 1e18;
    if (eth >= 1) return `${eth.toFixed(4)} ETH`;
    if (eth >= 0.0001) return `${eth.toFixed(6)} ETH`;
    return `${eth.toFixed(8)} ETH`;
  } catch {
    return null;
  }
}

export default function SubscriptionTerminal({
  forced = false,
  onActivate,
  onClose,
}: SubscriptionTerminalProps) {
  const { wallet, signer, connectWallet } = useWallet();
  const { isAuthenticated, loginWithWallet } = useAuth();
  const {
    plans,
    subscription,
    usage,
    error,
    isLoadingPlans,
    createPaymentIntent,
    verifyPayment,
    attachTxHash,
    startPaymentPolling,
    stopPaymentPolling,
    refreshAll,
    getPrice,
  } = useSubscription();

  const [billingPeriod, setBillingPeriod] = useState<BillingPeriod>('monthly');
  const [selectedPlan, setSelectedPlan] = useState<Exclude<PlanId, 'free'> | null>(null);
  const [step, setStep] = useState<PaymentStep>('select');
  const [paymentIntent, setPaymentIntent] = useState<PaymentIntent | null>(null);
  const [txHash, setTxHash] = useState('');
  const [errorMessage, setErrorMessage] = useState<string | null>(error);
  const [elapsedSeconds, setElapsedSeconds] = useState(0);
  const [currency, setCurrency] = useState<PaymentCurrency>('USDC');
  const [isPreparingPayment, setIsPreparingPayment] = useState(false);
  const [isManualVerifying, setIsManualVerifying] = useState(false);

  // Reset state on wallet change
  useEffect(() => {
    if (!wallet) {
      setStep('select');
      setSelectedPlan(null);
      setPaymentIntent(null);
      setTxHash('');
      setErrorMessage(null);
    }
  }, [wallet]);

  // Cleanup polling on unmount
  useEffect(() => {
    return () => {
      stopPaymentPolling();
    };
  }, [stopPaymentPolling]);

  // Elapsed-time counter during verification step
  useEffect(() => {
    if (step !== 'verify') {
      setElapsedSeconds(0);
      return;
    }
    setElapsedSeconds(0);
    const interval = setInterval(() => {
      setElapsedSeconds((prev) => prev + 1);
    }, 1000);
    return () => clearInterval(interval);
  }, [step]);

  // Propagate top-level hook error
  useEffect(() => {
    if (error) setErrorMessage(error);
  }, [error]);

  const handleSelectPlan = useCallback(
    (planId: Exclude<PlanId, 'free'>) => {
      if (subscription?.status === 'active' && subscription.plan === planId) {
        return;
      }
      setSelectedPlan(planId);
      setStep('select');
      // Each paid plan has a fixed billing interval — derive it from the plan.
      const planDef = plans?.plans.find((p) => p.id === planId);
      if (planDef?.billingPeriod) setBillingPeriod(planDef.billingPeriod);
      // Monetization funnel: track plan selection (P2 foundation). Price is intentionally
      // omitted — plan pricing is served separately (plans response) and must not be
      // duplicated/fabricated here.
      void import('../lib/analytics').then(({ trackEvent }) =>
        trackEvent('checkout_started', {
          plan: planId,
          billing_period: planDef?.billingPeriod,
        }),
      ).catch(() => {});
      // Reset payment state when switching plans
      setPaymentIntent(null);
      setTxHash('');
      setErrorMessage(null);
    },
    [subscription, plans],
  );

  const handleCreatePaymentIntent = async () => {
    if (!selectedPlan || !wallet || isPreparingPayment) return;

    setIsPreparingPayment(true);
    setErrorMessage(null);

    try {
      if (!isAuthenticated) {
        try {
          await loginWithWallet();
        } catch {
          setErrorMessage('Login required to subscribe — please sign in with your wallet first.');
          return;
        }
      }

      setStep('paying');

      const planDef = plans?.plans.find((p) => p.id === selectedPlan);
      const period = planDef?.billingPeriod ?? 'monthly';
      const intent = await createPaymentIntent(selectedPlan, period, currency);
      if (!intent) {
        setStep('error');
        return;
      }
      setPaymentIntent(intent);

      // Initiate wallet transaction with selected currency
      await executeWalletTransaction(intent);
    } catch {
      setStep('error');
    } finally {
      setIsPreparingPayment(false);
    }
  };

  /**
   * Send native ETH to the treasury. The exact wei amount comes from the
   * backend intent (locked at creation from the hourly-refreshed USD price) —
   * the frontend never computes the ETH amount itself.
   */
  const executeEthTransaction = async (intent: PaymentIntent) => {
    if (!signer) {
      setErrorMessage('Wallet signer not available');
      setStep('error');
      return;
    }
    if (!intent.amountToken) {
      setErrorMessage('ETH payment amount not available — please try again.');
      setStep('error');
      return;
    }

    try {
      const valueInWei = BigInt(intent.amountToken);
      const ethTx = await signer.sendTransaction({
        to: intent.treasuryAddress,
        value: valueInWei,
      });
      setTxHash(ethTx.hash);
      setPaymentIntent(intent);
      setStep('verify');

      // Wait for at least 1 confirmation before asking the API to verify.
      await ethTx.wait();

      // Tell the backend which tx to verify so its sweep can auto-confirm.
      const attached = await attachTxHash(intent.paymentId, ethTx.hash);
      if (!attached) {
        setErrorMessage(
          'Could not register the transaction with the server. Use “verify manually” below.',
        );
      }

      startPaymentPolling(
        intent.paymentId,
        () => {
          setStep('success');
          refreshAll();
          if (onActivate) onActivate();
        },
        600_000,
        ethTx.hash,
      );
    } catch (err: any) {
      if (err.code === 'ACTION_REJECTED') {
        setErrorMessage('Transaction rejected by user');
      } else if (err.code === 'UNPREDICTABLE_GAS_LIMIT') {
        setErrorMessage('Transaction may fail — check gas settings');
      } else if (String(err?.message || '').includes('insufficient funds')) {
        setErrorMessage('Insufficient ETH balance for this payment plus gas.');
      } else {
        setErrorMessage(err.reason || err.message || 'Transaction failed');
      }
      setStep('error');
    }
  };

  const executeWalletTransaction = async (intent: PaymentIntent) => {
    if (!signer) {
      setErrorMessage('Wallet signer not available');
      setStep('error');
      return;
    }

    try {
      const selectedCurrency = intent.selectedCurrency || currency;

      if (selectedCurrency === 'ETH') {
        await executeEthTransaction(intent);
        return;
      }

      const tokenAddress = resolveTokenAddress(intent, selectedCurrency);

      // Integer math: 1 USD cent = 10_000 token base units (6 decimals).
      const tokenAmount = BigInt(intent.amountCents) * 10_000n;

      const erc20Abi = [
        'function approve(address spender, uint256 amount) returns (bool)',
        'function transfer(address to, uint256 amount) returns (bool)',
      ];
      const tokenContract = new ethers.Contract(tokenAddress, erc20Abi, signer);

      // Approve is only required for transferFrom flows; we still prompt it
      // so wallets that batch or UX-copy expect a spending-cap step first.
      setStep('paying');
      const approveTx = await tokenContract.approve(intent.treasuryAddress, tokenAmount);
      await approveTx.wait();

      const transferTx = await tokenContract.transfer(intent.treasuryAddress, tokenAmount);
      setTxHash(transferTx.hash);
      setPaymentIntent(intent);
      setStep('verify');

      // Wait for at least 1 confirmation before asking the API to verify.
      await transferTx.wait();

      // Tell the backend which tx to verify so its sweep can auto-confirm.
      const attached = await attachTxHash(intent.paymentId, transferTx.hash);
      if (!attached) {
        setErrorMessage(
          'Could not register the transaction with the server. Use “verify manually” below.',
        );
      }

      startPaymentPolling(
        intent.paymentId,
        () => {
          setStep('success');
          refreshAll();
          if (onActivate) onActivate();
        },
        600_000,
        transferTx.hash,
      );
    } catch (err: any) {
      if (err.code === 'ACTION_REJECTED') {
        setErrorMessage('Transaction rejected by user');
      } else if (err.code === 'UNPREDICTABLE_GAS_LIMIT') {
        setErrorMessage('Transaction may fail — check gas settings');
      } else {
        setErrorMessage(err.reason || err.message || 'Transaction failed');
      }
      setStep('error');
    }
  };

  const handleManualVerification = async () => {
    if (!paymentIntent || !txHash || isManualVerifying) return;
    setErrorMessage(null);
    setIsManualVerifying(true);

    // Stop the auto-poller so we don't race with the manual verify
    stopPaymentPolling();

    try {
      const result = await verifyPayment(paymentIntent.paymentId, txHash);
      if (result.ok) {
        setStep('success');
        await refreshAll();
        if (onActivate) onActivate();
        return;
      }
      setErrorMessage(
        result.message ||
          'Verification failed — the transaction may need more confirmations. Wait a minute and try again.',
      );
      // Resume polling so confirmation can still land without another click.
      startPaymentPolling(
        paymentIntent.paymentId,
        () => {
          setStep('success');
          refreshAll();
          if (onActivate) onActivate();
        },
        600_000,
        txHash,
      );
    } catch (err: any) {
      setErrorMessage(err?.message || 'Verification request failed. Try again.');
    } finally {
      setIsManualVerifying(false);
    }
  };

  const handleClose = useCallback(() => {
    stopPaymentPolling();
    if (!forced && onClose) onClose();
  }, [forced, onClose, stopPaymentPolling]);

  // ─── Derived data ────────────────────────────────────────────────────────

  const currentSubscription = subscription;
  const isCurrentPlan = (planId: PlanId): boolean => {
    return (
      currentSubscription?.status === 'active' &&
      currentSubscription.plan === planId
    );
  };

  const expirationDate = currentSubscription?.expiresAt
    ? new Date(currentSubscription.expiresAt)
    : null;

  const getHighlightPlan = (): Exclude<PlanId, 'free'> | null => null;

  const highlightPlan = getHighlightPlan();

  const MAX_SAFE = Number.MAX_SAFE_INTEGER;
  /**
   * Finite quota rows for a plan definition. Windows set to
   * MAX_SAFE_INTEGER (not applicable to a plan) are hidden; the lifetime
   * `total` cap is shown when present. The Unlimited plan presents itself as
   * "Unlimited" in the UI — its numeric abuse/rate limits are enforced
   * server-side and never displayed on the card.
   */
  const quotaRows = (planDef: PlanDefinition): { label: string; value: string }[] => {
    if (planDef.id === 'unlimited') {
      return [{ label: 'Prompts', value: 'Unlimited' }];
    }
    const rows: { label: string; value: string }[] = [];
    const fmt = (n: number | null | undefined) =>
      n === null || n === undefined || n >= MAX_SAFE ? null : `${n} prompts`;
    const daily = fmt(planDef.quotas.daily);
    const weekly = fmt(planDef.quotas.weekly);
    const monthly = fmt(planDef.quotas.monthly);
    if (daily) rows.push({ label: 'Daily', value: daily });
    if (weekly) rows.push({ label: 'Weekly', value: weekly });
    if (monthly) rows.push({ label: 'Monthly', value: monthly });
    if (planDef.quotas.total !== null && planDef.quotas.total !== undefined) {
      rows.push({ label: 'Total (lifetime)', value: `${planDef.quotas.total} prompts` });
    }
    return rows;
  };

  // ─── Plan rendering ──────────────────────────────────────────────────────

  const renderFreePlan = () => {
    const isActive = isCurrentPlan('free');
    const planDef = plans?.plans.find((p) => p.id === 'free');

    return (
      <div
        onClick={handleClose}
        className={`relative rounded-xl border-2 p-4 h-full cursor-pointer transition-all ${
          isActive
            ? 'border-primary-600 bg-primary-50 dark:bg-primary-900/10'
            : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-gray-300 dark:hover:border-gray-600'
        }`}
      >
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-lg font-semibold text-heading">Free</h3>
          </div>
          <div className="text-right">
            <span className="text-2xl font-bold text-heading">$0</span>
          </div>
        </div>

        {isActive && (
          <div className="mb-3 flex items-center gap-1.5 text-primary-600 dark:text-primary-400 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Current plan
          </div>
        )}

        {expirationDate && (
          <p className="text-xs text-body mb-3">
            Current plan active until{' '}
            {expirationDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        )}

        {planDef && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
            <ul className="space-y-1.5 text-sm text-body">
              {quotaRows(planDef).map((row) => (
                <li key={row.label} className="flex items-center justify-between">
                  <span>{row.label}</span>
                  <span className="font-medium text-heading">{row.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  const renderPaidPlan = (planId: Exclude<PlanId, 'free'>) => {
    const planDef = plans?.plans.find((p) => p.id === planId);
    const fixedPeriod = planDef?.billingPeriod;
    const price = planDef && plans && fixedPeriod
      ? getPrice(plans, planId, fixedPeriod)
      : null;
    const isActive = isCurrentPlan(planId);
    const isHighlighted = planId === highlightPlan;
    const isSelected = selectedPlan === planId;
    const metal = PLAN_METAL[planId];

    // Each paid plan has a single fixed billing interval.
    const displayPrice = price?.usdDisplay;
    const displaySuffix = periodSuffix(fixedPeriod);

    const cardBase = isSelected
      ? metal.card
      : isHighlighted
        ? 'border-primary-600 dark:border-primary-400 ring-1 ring-primary-600/20 bg-white dark:bg-gray-800'
        : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800';

    return (
      <div
        onClick={() => handleSelectPlan(planId)}
        className={`relative rounded-xl border-2 p-4 h-full cursor-pointer transition-all ${cardBase} ${
          isActive && !isSelected
            ? 'bg-primary-50 dark:bg-primary-900/10'
            : !isSelected
              ? 'hover:border-primary-300 dark:hover:border-primary-600'
              : ''
        }`}
      >
        {/* Highlight label */}
        {isHighlighted && !isActive && (
          <div className="mb-2">
            <span className="inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300">
              <Zap className="w-3 h-3" />
              Popular
            </span>
          </div>
        )}

        <div className="flex items-center justify-between mb-2">
          <h3 className="text-lg font-semibold text-heading">{planDef?.name}</h3>
          <div className="text-right flex flex-col items-end">
            <span className="text-2xl font-bold text-heading">
              {displayPrice}
            </span>
            <span className="text-base text-body opacity-80 leading-tight mt-0.5 font-medium">
              {displaySuffix}
            </span>
          </div>
        </div>

        {isActive && (
          <div className="mb-3 flex items-center gap-1.5 text-primary-600 dark:text-primary-400 text-sm font-medium">
            <CheckCircle2 className="w-4 h-4" />
            Current plan
          </div>
        )}

        {expirationDate && !isActive && (
          <p className="text-xs text-body mb-3">
            Current plan active until{' '}
            {expirationDate.toLocaleDateString('en-US', {
              month: 'short',
              day: 'numeric',
              year: 'numeric',
            })}
          </p>
        )}

        {planDef && (
          <div className="mt-3 pt-3 border-t border-gray-100 dark:border-gray-700 space-y-2">
            <ul className="space-y-1.5 text-sm text-body">
              {quotaRows(planDef).map((row) => (
                <li key={row.label} className="flex items-center justify-between">
                  <span>{row.label}</span>
                  <span className="font-medium text-heading">{row.value}</span>
                </li>
              ))}
            </ul>
          </div>
        )}

        {isSelected && !isActive && (
          <div className={`mt-3 flex items-center gap-1.5 text-sm font-medium ${metal.accent}`}>
            <ArrowRight className="w-3.5 h-3.5" />
            Selected — configure below
          </div>
        )}
      </div>
    );
  };

  // ─── Payment states ──────────────────────────────────────────────────────

  const renderPaymentPaying = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-8">
        <Loader2 className="w-14 h-14 animate-spin text-primary-600 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-heading text-center mb-2">Confirm in Your Wallet</h3>
        <p className="text-sm text-body max-w-sm mx-auto text-center">
          Please approve the transaction in your wallet popup. Do not close this page.
        </p>
      </div>
    </div>
  );

  const renderPaymentVerify = () => {
    const minutes = Math.floor(elapsedSeconds / 60);
    const seconds = elapsedSeconds % 60;
    const timeStr = `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-8">
          <div className="relative w-20 h-20 mx-auto mb-4">
            <Loader2 className="w-20 h-20 animate-spin text-primary-600" />
            <div className="absolute inset-0 flex items-center justify-center">
              <span className="text-xs font-semibold text-heading">{timeStr}</span>
            </div>
          </div>
          <h3 className="text-lg font-semibold text-heading text-center mb-2">
            Confirming your payment
          </h3>
          <p className="text-sm text-body mb-4 max-w-sm mx-auto text-center">
            Waiting for blockchain confirmation… mainnet usually needs about a minute
            (several block confirmations).
            <br />
            Please keep this page open.
          </p>
          {errorMessage && (
            <div className="mb-4 rounded-lg border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-900/20 px-3 py-2 text-sm text-amber-800 dark:text-amber-200 text-center">
              {errorMessage}
            </div>
          )}
          {txHash && (
            <div className="flex flex-col items-center gap-2">
              <a
                href={explorerTxUrl(txHash, paymentIntent?.chainId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-xs font-mono break-all"
              >
                {txHash.slice(0, 10)}…{txHash.slice(-8)}
                <ArrowRight className="w-3 h-3" />
              </a>
              <button
                type="button"
                onClick={handleManualVerification}
                disabled={isManualVerifying || !paymentIntent}
                className="text-xs text-body underline hover:text-heading transition-colors disabled:opacity-50 disabled:no-underline disabled:cursor-wait"
              >
                {isManualVerifying ? 'Verifying…' : 'I already confirmed — verify manually'}
              </button>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPaymentSuccess = () => {
    const expDate = subscription?.expiresAt
      ? new Date(subscription.expiresAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
      : null;
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="relative bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-8">
          <button
            onClick={handleClose}
            aria-label="Close"
            className="absolute top-3 right-3 text-body hover:text-heading transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
          <div className="w-16 h-16 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-10 h-10 text-green-600 dark:text-green-400" />
          </div>
          <h3 className="text-xl font-semibold text-heading text-center mb-2">Payment Confirmed!</h3>
          <p className="text-sm text-body mb-4 text-center">
            Your pre-paid subscription is now active{expDate ? ` until ${expDate}` : ''}. Enjoy!
          </p>
          {paymentIntent && (
            <div className="flex justify-center">
              <a
                href={explorerTxUrl(txHash, paymentIntent?.chainId)}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 text-primary-600 hover:text-primary-700 text-xs font-mono mb-4"
              >
                View on Etherscan
                <ArrowRight className="w-3 h-3" />
              </a>
            </div>
          )}
        </div>
      </div>
    );
  };

  const renderPaymentError = () => (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-6">
        <div className="space-y-4">
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-4">
            <div className="flex items-start gap-3">
              <AlertCircle className="w-5 h-5 text-red-600 dark:text-red-400 flex-shrink-0 mt-0.5" />
              <div>
                <h3 className="text-sm font-semibold text-red-800 dark:text-red-300 mb-1">Payment Failed</h3>
                <p className="text-sm text-red-700 dark:text-red-400">
                  {errorMessage || 'Something went wrong with your payment.'}
                </p>
              </div>
            </div>
          </div>

          <button
            onClick={() => {
              setStep('select');
              setErrorMessage(null);
              if (!paymentIntent) return;
            }}
            className="w-full py-2.5 text-sm font-medium text-body bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    </div>
  );

  // ─── Main render ─────────────────────────────────────────────────────────

  if (!wallet) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-gray-100 dark:bg-gray-800 rounded-lg">
              <Lock className="w-5 h-5 text-body" />
            </div>
            <h2 className="text-lg font-semibold text-heading">Wallet Required</h2>
          </div>
          <p className="text-sm text-body mb-6">
            Connect your wallet to view and get a premium plan.
          </p>
          <button
            onClick={async () => {
              try {
                await connectWallet();
              } catch {
                setErrorMessage('Wallet connection failed. Please try again.');
              }
            }}
            className="w-full py-2.5 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 hover:from-primary-700 hover:to-secondary-700 rounded-lg transition-all flex items-center justify-center gap-2"
          >
            <Lock className="w-4 h-4" />
            Connect Wallet
          </button>
          {!forced && (
            <button
              onClick={handleClose}
              className="mt-2 w-full py-2.5 text-sm font-medium text-body bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
            >
              Maybe Later
            </button>
          )}
        </div>
      </div>
    );
  }

  if (isLoadingPlans) {
    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-md w-full p-8">
          <Loader2 className="w-10 h-10 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-center text-sm text-body">Loading plans...</p>
        </div>
      </div>
    );
  }

  // ─── Active subscription info overlay ────────────────────────────────────
  // Free users selecting a paid plan fall through to the select/payment view.
  // Keep payment steps (paying/verify/success/error) above this overlay so a
  // still-active free plan cannot steal the confirm screen mid-flow.
  const isFreePlan = currentSubscription?.plan === 'free';
  const showActiveOverlay =
    currentSubscription?.status === 'active' &&
    step === 'select' &&
    !(isFreePlan && selectedPlan);

  if (showActiveOverlay) {
    const activeDef = plans?.plans.find((p) => p.id === currentSubscription.plan);
    const billingPeriodLabel =
      activeDef?.billingPeriod === 'weekly'
        ? 'Weekly'
        : activeDef?.billingPeriod === 'yearly'
          ? 'Yearly'
          : activeDef?.billingPeriod === 'monthly'
            ? 'Monthly'
            : null;
    const usageWindows = usage
      ? ([
          { key: 'daily', label: 'Today' },
          { key: 'weekly', label: 'This Week' },
          { key: 'monthly', label: 'This Month' },
          { key: 'total', label: 'All time' },
        ] as const).filter(({ key }) => {
          const w = usage.usage[key];
          return !!w && w.limit < Number.MAX_SAFE_INTEGER && w.limit > 0;
        })
      : [];

    return (
      <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
        <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-lg w-full max-h-[90vh] overflow-y-auto p-6">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-heading">Your current plan</h2>
            <button
              onClick={handleClose}
              className="text-body hover:text-heading transition-colors"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="mb-6 p-4 bg-gradient-to-r from-primary-600 to-secondary-600 rounded-xl text-white">
            <div className="flex items-center justify-between">
              <span className="text-2xl font-bold capitalize text-white">{currentSubscription.plan}</span>
              {!isFreePlan && billingPeriodLabel && (
                <span className="text-sm font-medium opacity-90 text-white">{billingPeriodLabel}</span>
              )}
            </div>
          </div>

          {!isFreePlan && expirationDate && (
            <div className="mb-4 p-3 bg-gray-50 dark:bg-gray-800 rounded-lg">
              <p className="text-xs text-body mb-1">Expires</p>
              <p className="text-sm font-medium text-heading">
                {expirationDate.toLocaleDateString('en-US', {
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric',
                  year: 'numeric',
                })}
              </p>
            </div>
          )}

          {usageWindows.length > 0 && (
            <div className="space-y-3 mb-6">
              <p className="text-sm font-medium text-heading">Usage This Period</p>
              {usageWindows.map(({ key, label }) => {
                const window = usage!.usage[key];
                const pct =
                  window.limit > 0 ? Math.min(100, (window.used / window.limit) * 100) : 0;
                const barColor =
                  pct >= 100
                    ? 'bg-red-500'
                    : pct >= 80
                      ? 'bg-yellow-500'
                      : 'bg-primary-500';
                return (
                  <div key={key}>
                    <div className="flex justify-between text-xs mb-1">
                      <span className="text-body">{label}</span>
                      <span className="text-heading font-medium">
                        {window.used} / {window.limit}
                      </span>
                    </div>
                    <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-1.5">
                      <div
                        className={`${barColor} h-1.5 rounded-full transition-all duration-300`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Free plan: show paid plans as upgrade CTA */}
          {isFreePlan && plans && (
            <div className="mb-6 space-y-3">
              <p className="text-sm font-medium text-heading">Upgrade your plan</p>
              <p className="text-xs text-body opacity-75">
                Choose a paid plan to unlock more prompts.
              </p>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                {(['starter', 'extensive', 'unlimited'] as const).map((planId) => {
                  const planDef = plans.plans.find((p) => p.id === planId);
                  if (!planDef) return null;
                  const fixedPeriod = planDef.billingPeriod;
                  const price = fixedPeriod ? getPrice(plans, planId, fixedPeriod) : null;
                  const displaySuffix = periodSuffix(fixedPeriod);
                  const isSelected = selectedPlan === planId;
                  const metal = PLAN_METAL[planId];
                  return (
                    <button
                      key={planId}
                      type="button"
                      onClick={() => handleSelectPlan(planId)}
                      className={`text-left rounded-xl border-2 p-3 transition-all ${
                        isSelected
                          ? metal.card
                          : 'border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 hover:border-primary-400 dark:hover:border-primary-600'
                      }`}
                    >
                      <p className="text-sm font-semibold text-heading capitalize">{planDef.name}</p>
                      <p className="mt-1 text-lg font-bold text-heading">
                        {price?.usdDisplay ?? '—'}
                        <span className="text-sm font-medium text-body opacity-80 ml-0.5">
                          {displaySuffix}
                        </span>
                      </p>
                      {fixedPeriod && (
                        <p className="mt-1 text-xs text-body opacity-60 capitalize">{fixedPeriod}</p>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Paid plan: transaction history */}
          {!isFreePlan && (
            <div className="mb-6">
              <PaymentHistory limit={5} />
            </div>
          )}

          <button
            onClick={handleClose}
            className="w-full py-2.5 text-sm font-medium text-white bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg hover:from-primary-700 hover:to-secondary-700 transition-all"
          >
            Close
          </button>
        </div>
      </div>
    );
  }

  // ─── Payment step views ──────────────────────────────────────────────────

  if (step === 'paying') return renderPaymentPaying();
  if (step === 'verify') return renderPaymentVerify();
  if (step === 'success') return renderPaymentSuccess();
  if (step === 'error') return renderPaymentError();

  // ─── Select view ─────────────────────────────────────────────────────────

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white dark:bg-gray-900 border-b border-gray-100 dark:border-gray-800 px-6 pt-6 pb-4 flex items-center justify-between z-10">
          <div>
            <h2 className="text-xl font-bold text-heading">Get a premium plan</h2>
            <p className="text-sm text-body opacity-75 mt-0.5">
              Upgrade to unlock more prompts
            </p>
          </div>
          {!forced && (
            <button
              onClick={handleClose}
              className="text-body hover:text-heading transition-colors p-1"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          )}
        </div>

        <div className="p-6 space-y-6">
          {/* Plan cards */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {/* Free plan */}
            <div className="h-full">
              {renderFreePlan()}
            </div>

            {/* Starter */}
            {plans && (
              <div className="h-full">
                {renderPaidPlan('starter')}
              </div>
            )}

            {/* Extensive */}
            {plans && (
              <div className="h-full">
                {renderPaidPlan('extensive')}
              </div>
            )}

            {/* Unlimited */}
            {plans && (
              <div className="h-full">
                {renderPaidPlan('unlimited')}
              </div>
            )}
          </div>

          {/* Payment CTA for selected plan */}
          {selectedPlan && step === 'select' && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-6 space-y-4">
              <div className={`rounded-lg p-4 space-y-3 border ${PLAN_METAL[selectedPlan].section}`}>
                {wallet && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-body">Wallet</span>
                    <span className="text-heading font-mono text-xs">
                      {wallet.address.slice(0, 6)}…{wallet.address.slice(-4)}
                    </span>
                  </div>
                )}
                <div className="flex items-center justify-between text-sm">
                  <span className="text-body">Plan</span>
                  <span className="text-heading font-medium capitalize">{selectedPlan}</span>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-body">Billing</span>
                  <span className="text-heading font-medium capitalize">{billingPeriod}</span>
                </div>

                {/* Currency selector — shown before intent is created */}
                {!paymentIntent && wallet && (
                  <div className="space-y-2">
                    <span className="text-sm text-body">Pay with</span>
                    <div className="grid grid-cols-3 gap-2">
                      <button
                        type="button"
                        onClick={() => setCurrency('USDC')}
                        className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                          currency === 'USDC'
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'bg-white dark:bg-gray-700 text-body border border-gray-200 dark:border-gray-600 hover:border-primary-400'
                        }`}
                      >
                        <UsdcIcon className="w-5 h-5 shrink-0" /> USDC
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrency('USDT')}
                        className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                          currency === 'USDT'
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'bg-white dark:bg-gray-700 text-body border border-gray-200 dark:border-gray-600 hover:border-primary-400'
                        }`}
                      >
                        <UsdtIcon className="w-5 h-5 shrink-0" /> USDT
                      </button>
                      <button
                        type="button"
                        onClick={() => setCurrency('ETH')}
                        className={`flex items-center justify-center gap-2 py-2.5 px-3 rounded-lg text-sm font-medium transition-all ${
                          currency === 'ETH'
                            ? 'bg-primary-600 text-white shadow-sm'
                            : 'bg-white dark:bg-gray-700 text-body border border-gray-200 dark:border-gray-600 hover:border-primary-400'
                        }`}
                      >
                        <EthIcon className="w-5 h-5 shrink-0" /> ETH
                      </button>
                    </div>
                    <div className="flex items-center justify-between text-xs text-body">
                      <span>Network</span>
                      <span>{SUPPORTED_NETWORK_NAME}</span>
                    </div>
                  </div>
                )}

                {paymentIntent && (
                  <>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-body">Amount</span>
                      <span className="text-heading font-semibold">{paymentIntent.usdDisplay}</span>
                    </div>
                    {paymentIntent.selectedCurrency === 'ETH' ? (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-body">You send</span>
                        <span className="text-heading font-medium">
                          {formatEthAmount(paymentIntent.amountToken) ?? '—'}
                        </span>
                      </div>
                    ) : null}
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-body">Currency</span>
                      <span className="text-heading font-medium">{paymentIntent.selectedCurrency || currency}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-body">
                      <span>Network</span>
                      <span>{SUPPORTED_NETWORK_NAME}</span>
                    </div>
                    <div className="flex items-center justify-between text-xs text-body">
                      <span>Recipient</span>
                      <span className="font-mono">
                        {paymentIntent.treasuryAddress.slice(0, 6)}…{paymentIntent.treasuryAddress.slice(-4)}
                      </span>
                    </div>
                  </>
                )}
              </div>

              {!wallet ? (
                <button
                  onClick={async () => {
                    try { await connectWallet(); } catch { setErrorMessage('Wallet connection failed.'); }
                  }}
                  className="w-full py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Connect Wallet to Continue
                </button>
              ) : !isAuthenticated ? (
                <button
                  onClick={async () => {
                    try { await loginWithWallet(); } catch { setErrorMessage('Login failed — please try again.'); }
                  }}
                  className="w-full py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg transition-all flex items-center justify-center gap-2"
                >
                  <Lock className="w-4 h-4" />
                  Sign In to Subscribe
                </button>
              ) : (
                <button
                  onClick={handleCreatePaymentIntent}
                  disabled={isPreparingPayment}
                  aria-busy={isPreparingPayment}
                  className="w-full py-3 text-sm font-semibold text-white bg-gradient-to-r from-primary-600 to-secondary-600 rounded-lg transition-all flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
                >
                  {isPreparingPayment ? (
                    <>
                      <Loader2 className="w-4 h-4 animate-spin" />
                      Preparing payment…
                    </>
                  ) : (
                    <>
                      <Lock className="w-4 h-4" />
                      {!paymentIntent
                        ? `Pay with ${currency}`
                        : paymentIntent.selectedCurrency === 'ETH'
                          ? `Pay ${formatEthAmount(paymentIntent.amountToken) ?? 'ETH'}`
                          : `Pay ${paymentIntent.usdDisplay} with ${paymentIntent.selectedCurrency || currency}`}
                    </>
                  )}
                </button>
              )}

              {!wallet && (
                <p className="text-xs text-center text-body opacity-55">
                  Wallet connection required to subscribe.
                </p>
              )}
              {wallet && !paymentIntent && (
                <p className="text-xs text-center text-body opacity-55">
                  Select USDC, USDT or ETH above, then click Pay to create your payment.
                </p>
              )}
              {wallet && paymentIntent && (
                <p className="text-xs text-center text-body opacity-55">
                  You will be asked to approve and confirm the transaction in your wallet.
                </p>
              )}
            </div>
          )}

          {/* Manual verification if txHash already submitted */}
          {txHash && step === 'select' && paymentIntent && (
            <div className="border-t border-gray-200 dark:border-gray-700 pt-4 space-y-3">
              <p className="text-sm text-body">Already sent?</p>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={txHash}
                  readOnly
                  className="flex-1 px-3 py-2 text-xs font-mono border border-gray-300 dark:border-gray-600 rounded-lg bg-gray-50 dark:bg-gray-800 text-heading"
                />
                <button
                  onClick={handleManualVerification}
                  className="px-4 py-2 text-sm font-medium text-white bg-gray-700 hover:bg-gray-800 dark:bg-gray-600 dark:hover:bg-gray-500 rounded-lg transition-colors"
                >
                  Verify
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}